/**
 * InventoryPickerDialog
 *
 * Picker over the product catalog (`cr001_productses`) for adding
 * line items to a quote. Scoped by category by default — the scope
 * mirrors what the quote type typically draws from:
 *
 *   - replacement → Hardware
 *   - repair      → Labour + Hardware
 *   - m365        → M365 Licensing
 *
 * "Show all" clears the scope. Free-text search is OR'd against the
 * server's product search across SKU + display name + description.
 *
 * On pick, the caller receives a `ProductsListItem` and is responsible
 * for translating it into a QuoteItem + QuoteOption.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import type { ProductsListItem, QuoteType } from '@techgitter/contracts';
import { fetchInventory } from '../../services/inventoryApi';

interface InventoryPickerDialogProps {
  open: boolean;
  onClose: () => void;
  quoteType: QuoteType;
  onPick: (product: ProductsListItem) => void;
}

const ALL = '__all__' as const;

// Category options surfaced in the dropdown. Order chosen so that the
// most common picks for repair / m365 are near the top.
const CATEGORY_OPTIONS: { value: string; label: string }[] = [
  { value: 'hardware', label: 'Hardware' },
  { value: 'support-labour', label: 'Labour' },
  { value: 'support-labour-ah', label: 'Labour (After Hours)' },
  { value: 'licensing-m365', label: 'M365 Licensing' },
  { value: 'licensing-google', label: 'Google Licensing' },
  { value: 'domain', label: 'Domain' },
  { value: 'hosting', label: 'Hosting' },
  { value: 'other', label: 'Other' },
];

// Default scope per quote type. `repair` gets two categories — we
// fire two requests and merge — because techs typically quote a
// labour line plus a part. Other types map to a single category.
const DEFAULT_CATEGORIES_BY_TYPE: Record<QuoteType, string[]> = {
  new_laptop: ['hardware'],
  new_desktop: ['hardware'],
  new_desktop_custom: ['hardware'],
  new_all_in_one: ['hardware'],
  repair: ['support-labour', 'hardware'],
  network_install: ['support-labour', 'hardware'],
  m365: ['licensing-m365'],
  replacement: ['hardware'],
};

const fmtCurrency = (n: number): string =>
  n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });

export const InventoryPickerDialog: React.FC<InventoryPickerDialogProps> = ({
  open,
  onClose,
  quoteType,
  onPick,
}) => {
  // Category defaults to "Show all" — most inventory rows don't carry a
  // populated cr001_product_category, so filtering would return nothing
  // useful on first open. Revisit once the catalog is properly tagged.
  // DEFAULT_CATEGORIES_BY_TYPE is kept for the per-type alt-shortcut.
  const altCategory = DEFAULT_CATEGORIES_BY_TYPE[quoteType][1];

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [category, setCategory] = useState<string>(ALL);
  const [items, setItems] = useState<ProductsListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset transient state when reopened so a stale search doesn't
  // leak between sessions.
  useEffect(() => {
    if (open) {
      setSearch('');
      setDebouncedSearch('');
      setCategory(ALL);
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => window.clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetchInventory({
      page: 1,
      pageSize: 50,
      search: debouncedSearch || undefined,
      category: category === ALL ? undefined : category,
      sort: 'displayName',
      order: 'asc',
      signal: controller.signal,
    })
      .then((resp) => setItems(resp.items))
      .catch((err) => {
        if ((err as { name?: string }).name === 'AbortError') return;
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [open, debouncedSearch, category]);

  const showAltShortcut = altCategory && category !== altCategory && category !== ALL;

  const dialogTitle = useMemo(() => {
    switch (quoteType) {
      case 'repair': return 'Add from Inventory — Repair';
      case 'm365': return 'Add from Inventory — M365';
      default: return 'Add from Inventory';
    }
  }, [quoteType]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent dividers>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Search inventory…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            select
            size="small"
            label="Category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            {CATEGORY_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
            <MenuItem value={ALL}>Show all</MenuItem>
          </TextField>
        </Stack>

        {showAltShortcut && (
          <Box sx={{ mb: 2 }}>
            <Button
              size="small"
              variant="text"
              onClick={() => setCategory(altCategory!)}
            >
              Switch to{' '}
              {CATEGORY_OPTIONS.find((c) => c.value === altCategory)?.label ??
                altCategory}
            </Button>
          </Box>
        )}

        {error && (
          <Typography variant="body2" color="error" sx={{ mb: 1 }}>
            {error}
          </Typography>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : items.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            No products match.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {items.map((p) => (
              <Box
                key={p.id}
                role="button"
                tabIndex={0}
                onClick={() => {
                  onPick(p);
                  onClose();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onPick(p);
                    onClose();
                  }
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  px: 1.5,
                  py: 1,
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: 'action.hover' },
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {p.displayName}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {p.sku}
                  </Typography>
                </Box>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
                  <Chip label={p.category || 'other'} size="small" variant="outlined" />
                  <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 110, textAlign: 'right' }}>
                    {fmtCurrency(p.unitCost)} inc-GST
                  </Typography>
                </Stack>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
